import { useState } from 'react';
import type { Friend, Group } from '../types';

type Props = {
  friends: Friend[];
  onCreate: (group: Group) => void;
  onBack: () => void;
};

const GROUP_CAP = 16;

export function GroupCreate({ friends, onCreate, onBack }: Props) {
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function submit() {
    setError(null);
    const cleanName = name.trim();
    if (!cleanName) {
      setError('Pick a name for the group.');
      return;
    }
    if (selected.size === 0) {
      setError('Pick at least one friend to include.');
      return;
    }
    if (selected.size > GROUP_CAP - 1) {
      setError(`A group can have at most ${GROUP_CAP} people, including you.`);
      return;
    }
    const members = friends.filter((f) => selected.has(f.id));
    onCreate({
      id: crypto.randomUUID(),
      name: cleanName,
      members,
      created_at: new Date().toISOString(),
    });
  }

  return (
    <div className="screen">
      <header className="topbar">
        <button className="btn btn-ghost btn-small" onClick={onBack} aria-label="Back">
          ← Back
        </button>
        <div className="topbar-center">
          <span className="me-name">New group</span>
        </div>
        <span className="topbar-spacer" />
      </header>

      <main className="content">
        <section className="card">
          <h2 className="h-section">Make it small and intimate.</h2>
          <p className="muted">
            Up to {GROUP_CAP} people total, including you. No notifications. No
            read receipts. No "X is typing." Same calm, applied to a small circle.
          </p>

          <label className="field">
            <span className="field-label">Group name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sunday breakfast"
              maxLength={40}
            />
          </label>

          <p className="field-label" style={{ marginTop: 18 }}>
            Pick friends ({selected.size}/{GROUP_CAP - 1} max)
          </p>
          {friends.length === 0 ? (
            <p className="muted">
              You don't have any friends added yet. Add one or two first, then
              come back and group them.
            </p>
          ) : (
            <ul className="member-pick">
              {friends.map((f) => {
                const on = selected.has(f.id);
                return (
                  <li key={f.id}>
                    <button
                      type="button"
                      className={`member-pick-row ${on ? 'on' : ''}`}
                      onClick={() => toggle(f.id)}
                    >
                      <span className="friend-avatar friend-avatar-sm">
                        {f.username[0]?.toUpperCase() ?? '?'}
                      </span>
                      <span className="friend-text">
                        <span className="friend-name">@{f.username}</span>
                      </span>
                      <span className={`pick-bullet ${on ? 'on' : ''}`} aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {error && <p className="error">{error}</p>}

          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={!name.trim() || selected.size === 0}
            style={{ marginTop: 18 }}
          >
            Create group
          </button>
        </section>
      </main>
    </div>
  );
}
